import 'whatwg-fetch';
import { getCookie } from '@/utils';
import {
  BadRequestError,
  LoginTimedOutError,
  ResourceNotFoundError
} from '@api/Errors';

import ForbiddenErrorHandler from './Api/ErrorHandlers/ForbiddenErrorHandler';
import UnauthorizedErrorHandler from './Api/ErrorHandlers/UnauthorizedErrorHandler';
import InternalServerErrorHandler from './Api/ErrorHandlers/InternalServerErrorHandler';
import ServiceUnavailableErrorHandler from './Api/ErrorHandlers/ServiceUnavailableErrorHandler';

export default class Api {
  /**
   * Base class for defining API endpoint wrappers
   * @param {Object}  [options={}] - configuration for API client
   * @param {Number}  options.simulateResponseCode - will force Postman Mock API to return the specified error code
   * @param {String}  options.simulateExample - will force Postman Mock API to return the specified example
   */
  constructor(options = {}) {
    this.options = options;
    this.headers = new Headers();
    this._prepareHeaders();
    // Todo, this can go away eventually
    /* istanbul ignore next */
    this._setDevelopmentHeaders();
  }

  /**
   * Given a URI GETs the associated resource from the VUE_APP_API_BASE_URL specified in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} [queryParams={}] - query parameters to append to the URL
   * @returns {Promise} a promise object representing the deserialized JSON of the response body
   */
  get = (uri, queryParams = {}, isBeta) => {
    let url = this._prepareUrl(uri, queryParams, isBeta);
    return this._makeRequest(url, {
      method: 'GET',
      headers: this.headers
    });
  };

  /**
   * Given a URI POSTs the associated resource from the VUE_APP_API_BASE_URL specified in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} [body={}] - request body submitted with the POST request
   * @param {Object} [queryParams={}] - query parameters to append to the URL
   * @returns {Promise} a promise object representing the deserialized JSON of the response body
   */
  post = (uri, body = {}, queryParams = {}, isBeta) => {
    let url = this._prepareUrl(uri, queryParams, isBeta);
    return this._makeRequest(url, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: this.headers
    });
  };

  /**
   * Given a URI POSTs the associated resource from the VUE_APP_API_BASE_URL specified in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} [body={}] - request body submitted with the POST request
   * @param {Object} [queryParams={}] - query parameters to append to the URL
   * @returns {Promise} a promise object representing the deserialized JSON of the response body
   */
  postForm = (uri, body = {}, queryParams = {}, isBeta) => {
    let url = this._prepareUrl(uri, queryParams, isBeta);
    this.headers.delete('content-type');
    this._setHeader('X-XSRF-TOKEN', getCookie('XSRF-TOKEN'));
    return this._makeRequest(url, {
      method: 'POST',
      body: body,
      headers: this.headers
    });
  };

  /**
   * Given a URI POSTs the associated resource from the VUE_APP_API_BASE_URL specified in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} [body={}] - request body submitted with the POST request
   * @param {Object} [queryParams={}] - query parameters to append to the URL
   * @returns {Promise} a promise object representing the deserialized JSON of the response body
   */
  put = (uri, body = {}, queryParams = {}, isBeta) => {
    let url = this._prepareUrl(uri, queryParams, isBeta);
    return this._makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: this.headers
    });
  };

  /**
   * Given a URI DELETEs the associated resource from the VUE_APP_API_BASE_URL specified in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} [body={}] - request body submitted with the POST request
   * @param {Object} [queryParams={}] - query parameters to append to the URL
   * @returns {Promise} a promise object representing the deserialized JSON of the response body
   */
  delete = (uri, body = {}, queryParams = {}, isBeta) => {
    let url = this._prepareUrl(uri, queryParams, isBeta);
    return this._makeRequest(url, {
      method: 'DELETE',
      body: JSON.stringify(body),
      headers: this.headers
    });
  };

  /**
   * Returns a promise that resolves to the responseBody param
   * @param responseBody
   * @returns {Promise} a promise resolving to the responseBody
   */
  spoofResponse = (responseBody = {}) => {
    return new Promise((resolve, reject) => {
      resolve(responseBody);
    });
  };

  /**
   * Returns a URL for a remote resource whose host is defined by VUE_APP_API_BASE_URL in .env.[mode]
   * @param {String} uri - the resource location
   * @param {Object} queryParams - query parameters to append to the URL
   * @param {Boolean} isBeta - use the beta service URL
   * @returns {String}
   * @private
   */
  _prepareUrl = (uri, queryParams = {}, isBeta) => {
    const isBetaMode = isBeta === undefined ? false : isBeta;
    let url =
      isBetaMode === 'true'
        ? process.env.VUE_APP_BETA_BASE_URL
        : process.env.VUE_APP_API_BASE_URL;
    url += uri;
    Object.keys(queryParams).forEach((key, i) => {
      url += i === 0 ? '?' : '&';
      const urlEncodedVal = encodeURIComponent(queryParams[key]);
      url += `${key}=${urlEncodedVal}`;
    });
    return url;
  };

  /**
   * Sets the necessary headers for an API request upon instantiation of a new Api class or subclass
   * @private
   */
  _prepareHeaders = () => {
    this._setHeader(
      'content-type',
      process.env.VUE_APP_API_CONTENT_TYPE || 'application/json'
    );
    this._setHeader('X-XSRF-TOKEN', getCookie('XSRF-TOKEN'));
  };

  /**
   * Given two strings, sets a header with name and value given
   * @param {String} headerName - the name of the header being set
   * @param {String} headerValue - the value of the header being set
   * @private
   */
  _setHeader = (headerName, headerValue) => {
    this.headers.set(headerName, headerValue);
  };

  /**
   * Wraps `fetch` method and runs additional necessary logic for requests
   * @param {String} url - resource location
   * @param {Object} requestOptions - fetch configuration
   * @returns {Promise} a promise resolving to the response body
   * @private
   */
  _makeRequest = (url, requestOptions = {}) => {
    requestOptions.credentials = 'same-origin'; // fix for legacy browser's having default omit value https://www.npmjs.com/package/whatwg-fetch#sending-cookies

    return fetch(url, requestOptions)
      .then(response => this._verifyStatus(response))
      .then(response => response.json());
  };

  /**
   * Verifies the status of the response and throws any appropriate errors that may correspond to that status
   * @param {Promise.Response} response
   * @returns {Promise.Response}
   * @throws {ApiError} - throws an API Error subtype class depending on the response status code
   * @private
   */
  _verifyStatus = response => {
    switch (response.status) {
      case 400:
        throw new BadRequestError('400 Bad Request', response);
      case 401:
        return new UnauthorizedErrorHandler(response, this.options);
      case 403:
        return new ForbiddenErrorHandler(response);
      case 404:
        throw new ResourceNotFoundError('Resource not found.', response);
      case 440:
        throw new LoginTimedOutError('Login timed-out.', response);
      case 500:
        throw new InternalServerErrorHandler(response);
      case 503:
        throw new ServiceUnavailableErrorHandler(response);
      default:
        return response;
    }
  };

  // Todo, this can go away eventually
  /* eslint-disable */
  _setDevelopmentHeaders = /* istanbul ignore next */ () => {
    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'staging'
    ) {
      this._setHeader('x-postman-token', process.env.VUE_APP_POSTMAN_TOKEN);
      this._setHeader('x-api-key', process.env.VUE_APP_POSTMAN_API_KEY);
      if (this.options.simulateResponseCode)
        this._setHeader(
          'x-mock-response-code',
          this.options.simulateResponseCode
        );
      if (this.options.simulateExample)
        this._setHeader('x-mock-response-name', this.options.simulateExample);
    }
  };
  /* eslint-enable */
}
